import { Button, Checkbox, Input } from '@unlimitai/ui';
import { type ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import api from '@/apis/api';

import { errorMessage } from './error';
import styles from './index.module.scss';

type Choice = {
  kind: 'personal' | 'enterprise' | 'invite';
  inviteCode: string;
  organizationName: string;
  organizationDescription: string;
  confirmed: boolean;
};
type Intent = Choice & {
  enabled: true;
  canJoin: boolean;
  targetOrganizationName?: string;
  prepared: boolean;
  locked: boolean;
};
const endpoint = '/api/experience/business-intent';

const BusinessOnboarding = ({ children }: { readonly children: ReactNode }) => {
  const { isPreview } = useContext(PageContext);
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  const [intent, setIntent] = useState<Intent | { enabled: false }>();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const applicable = !isPreview && ['/sign-in', '/register'].includes(pathname);

  useEffect(() => {
    if (!applicable) {
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await api
          .get(endpoint, { signal: controller.signal, retry: 0 })
          .json<Intent | { enabled: false }>();
        setIntent(data);
        setEditing(data.enabled && !data.prepared);
        setError('');
      } catch (error_: unknown) {
        if (!controller.signal.aborted) {
          setError(await errorMessage(error_));
        }
      }
    };
    void load();
    return () => {
      controller.abort();
    };
  }, [applicable, attempt]);

  if (!applicable || intent?.enabled === false) {
    return children;
  }
  if (!intent) {
    return (
      <div className={styles.form} role="status">
        <p>{error || (zh ? '正在获取注册信息…' : 'Loading registration details…')}</p>
        {error && (
          <Button
            onClick={() => {
              setAttempt((value) => value + 1);
            }}
          >
            {zh ? '重试' : 'Retry'}
          </Button>
        )}
      </div>
    );
  }
  const update = (fields: Partial<Choice>) => {
    setIntent({ ...intent, ...fields });
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const { kind, inviteCode, organizationName, organizationDescription, confirmed } = intent;
      const result = await api
        .put(endpoint, {
          retry: 0,
          json: { kind, inviteCode, organizationName, organizationDescription, confirmed },
        })
        .json<Intent>();
      setIntent(result);
      setEditing(false);
    } catch (error_: unknown) {
      setError(await errorMessage(error_));
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <>
        <div className={styles.summary}>
          <span>
            {intent.kind === 'enterprise'
              ? intent.organizationName
              : intent.kind === 'invite'
                ? intent.targetOrganizationName
                : zh
                  ? '个人账号'
                  : 'Personal account'}
            {intent.inviteCode && ` · ${intent.inviteCode}`}
          </span>
          {!intent.locked && (
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(true);
              }}
            >
              {zh ? '邀请码 / 企业注册' : 'Referral / business signup'}
            </Button>
          )}
        </div>
        {children}
      </>
    );
  }
  return (
    <BusinessForm
      intent={intent}
      isChinese={zh}
      isBusy={busy}
      error={error}
      update={update}
      save={save}
    />
  );
};

const BusinessForm = ({
  intent,
  isChinese: zh,
  isBusy: busy,
  error,
  update,
  save,
}: {
  readonly intent: Intent;
  readonly isChinese: boolean;
  readonly isBusy: boolean;
  readonly error: string;
  readonly update: (fields: Partial<Choice>) => void;
  readonly save: () => Promise<void>;
}) => {
  return (
    <section className={styles.form}>
      <div className={styles.tabs}>
        {(['personal', 'enterprise', ...(intent.canJoin ? ['invite' as const] : [])] as const).map(
          (kind) => (
            <Button
              key={kind}
              type="button"
              variant={intent.kind === kind ? 'secondary' : 'ghost'}
              aria-pressed={intent.kind === kind}
              onClick={() => {
                update({ kind, confirmed: false });
              }}
            >
              {kind === 'personal'
                ? zh
                  ? '个人'
                  : 'Personal'
                : kind === 'enterprise'
                  ? zh
                    ? '企业注册'
                    : 'Business'
                  : zh
                    ? '邀请入组'
                    : 'Join team'}
            </Button>
          )
        )}
      </div>
      {intent.kind === 'enterprise' && (
        <>
          <label>
            {zh ? '企业 / 组织名称' : 'Organization name'}
            <Input
              maxLength={20}
              value={intent.organizationName}
              onChange={(event) => {
                update({ organizationName: event.target.value, confirmed: false });
              }}
            />
          </label>
          <label>
            {zh ? '组织简介（选填）' : 'Description (optional)'}
            <Input
              maxLength={500}
              value={intent.organizationDescription}
              onChange={(event) => {
                update({ organizationDescription: event.target.value });
              }}
            />
          </label>
        </>
      )}
      {intent.kind === 'invite' && (
        <p>
          {zh ? '完成认证后加入：' : 'Join after authentication: '}
          {intent.targetOrganizationName}
        </p>
      )}
      <label>
        {zh ? '推荐邀请码（选填）' : 'Referral code (optional)'}
        <Input
          maxLength={64}
          value={intent.inviteCode}
          onChange={(event) => {
            update({ inviteCode: event.target.value });
          }}
        />
      </label>
      <p className={styles.hint}>
        {zh
          ? '微信登录同样适用；邀请码仅在新账号注册时生效。'
          : 'Also works with WeChat. Referral codes apply only to new accounts.'}
      </p>
      {intent.kind !== 'personal' && (
        <label className={styles.confirm}>
          <Checkbox
            checked={intent.confirmed}
            onCheckedChange={(checked) => {
              update({ confirmed: checked === true });
            }}
          />
          {zh
            ? '确认认证后创建或加入此组织；一个账号只能加入一个组织。'
            : 'Confirm creating or joining this organization after authentication. One organization per account.'}
        </label>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <Button
        disabled={busy}
        onClick={() => {
          void save();
        }}
      >
        {zh ? '继续登录 / 注册' : 'Continue to sign in / register'}
      </Button>
    </section>
  );
};

export { default as BusinessCompletion } from './BusinessCompletion';
export default BusinessOnboarding;
